import { useEffect } from 'react';

const FOCUS_POLL_INTERVAL_MS = 30;
const FOCUS_POLL_MAX_ATTEMPTS = 10;

/**
 * Poll `.focus()` on a target input until it actually holds focus, or until we hit the attempt
 * cap.
 *
 * Chakra's Modal runs its own focus trap on mount. That trap fires asynchronously after a mount
 * effect, so a one-shot `.focus()` call on a target input is silently reverted to Chakra's default
 * target. Polling `.focus()` a handful of times over ~300 ms reliably beats the trap.
 */
export const useFocusInputOnMount = (
  getTarget: () => HTMLInputElement | HTMLTextAreaElement | null,
  isEnabled: boolean = true,
): void => {
  useEffect(() => {
    if (!isEnabled) return undefined;
    let attempts = 0;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryFocus = () => {
      const target = getTarget();
      if (target && document.activeElement !== target) {
        target.focus();
      }
      attempts += 1;
      return (
        attempts >= FOCUS_POLL_MAX_ATTEMPTS || document.activeElement === target
      );
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        if (!tryFocus()) scheduleNext();
      }, FOCUS_POLL_INTERVAL_MS);
    };
    if (!tryFocus()) scheduleNext();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };

    // getTarget is intentionally omitted from deps — callers pass an inline arrow that would
    // otherwise force the effect to re-run every render. isEnabled is the semantic trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);
};
